import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, Timer, AlertTriangle, MessageSquare, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/utils/locale";

function EmailVerifyBanner({ email, authAxios, onDismiss }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const resend = async () => {
    setSending(true);
    try {
      await authAxios.post("/auth/resend-verification");
      setSent(true);
      toast.success("Email de verificação enviado! Verifique a sua caixa de entrada.");
    } catch {
      toast.error("Erro ao enviar email. Tente novamente.");
    }
    setSending(false);
  };
  return (
    <Card className="border-blue-300 bg-blue-50" data-testid="email-verify-banner">
      <CardContent className="flex items-center justify-between py-3 px-4">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-5 h-5 text-blue-500 shrink-0" />
          <div>
            <p className="font-semibold text-blue-800 text-sm">Confirme o seu email para garantir acesso</p>
            <p className="text-xs text-blue-600">
              Enviamos um link para <strong>{email}</strong>. Sem email válido não será possível recuperar a conta.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100 h-8 text-xs"
            onClick={resend} disabled={sending || sent} data-testid="resend-verify-btn">
            {sending ? "A enviar..." : sent ? "Enviado!" : "Reenviar email"}
          </Button>
          <button onClick={onDismiss} className="text-blue-400 hover:text-blue-600" data-testid="dismiss-email-verify">
            <X className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardBanners({
  stats, restaurant, isSetupComplete,
  dismissedEmptyMenu, setDismissedEmptyMenu,
  dismissedEmailVerify, setDismissedEmailVerify,
  navigate, authAxios,
}) {
  return (
    <>
      {stats?.subscription_status === "past_due" && (
        <Card className="border-red-500/50 bg-red-50" data-testid="payment-warning">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <CreditCard className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="font-bold text-red-600">Pago pendiente</p>
                <p className="text-sm text-red-500/80">
                  {stats.days_until_suspension != null
                    ? `Quedan ${stats.days_until_suspension} día(s) para el bloqueo.`
                    : "Actualiza tu método de pago."}
                </p>
              </div>
            </div>
            <Button onClick={() => navigate("/settings?tab=subscription")} className="bg-red-600 hover:bg-red-700 shrink-0 gap-2" data-testid="pay-now-btn">
              <CreditCard className="w-4 h-4" />Pagar
            </Button>
          </CardContent>
        </Card>
      )}
      {stats?.subscription_status === "suspended" && (
        <Card className="border-red-600 bg-red-50" data-testid="suspended-warning">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-200 flex items-center justify-center shrink-0">
                <X className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="font-bold text-red-600">Cuenta suspendida</p>
                <p className="text-sm text-red-400">Bloqueada por falta de pago.</p>
              </div>
            </div>
            <Button onClick={() => navigate("/settings?tab=subscription")} className="bg-red-600 hover:bg-red-700 shrink-0" data-testid="reactivate-btn">
              Reactivar
            </Button>
          </CardContent>
        </Card>
      )}
      {stats?.subscription_status === "trial" && stats?.trial_days_left != null && (
        <Card className={`border ${stats.trial_days_left <= 7 ? "border-red-400 bg-red-50" : stats.trial_days_left <= 14 ? "border-amber-400 bg-amber-50" : "border-emerald-400 bg-emerald-50"}`} data-testid="trial-banner">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${stats.trial_days_left <= 7 ? "bg-red-100" : stats.trial_days_left <= 14 ? "bg-amber-100" : "bg-emerald-100"}`}>
                <Timer className={`w-6 h-6 ${stats.trial_days_left <= 7 ? "text-red-500" : stats.trial_days_left <= 14 ? "text-amber-500" : "text-emerald-600"}`} />
              </div>
              <div>
                <p className="font-semibold">{stats.trial_days_left} {t("trialDays")}</p>
                <p className="text-sm text-muted-foreground">Aprovecha todas las funciones. Elige un plan cuando estés listo.</p>
              </div>
            </div>
            <Button onClick={() => navigate("/settings?tab=subscription")} className="bg-emerald-600 hover:bg-emerald-700 shrink-0 gap-2" data-testid="trial-subscribe-btn">
              {t("viewPlans")} <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      )}
      {!dismissedEmptyMenu && isSetupComplete && (stats?.total_products ?? 1) === 0 && (
        <Card className="border-amber-400 bg-amber-50" data-testid="empty-menu-warning">
          <CardContent className="flex items-center justify-between py-3 px-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
              <div>
                <p className="font-semibold text-amber-800 text-sm">Menu vazio — clientes nao conseguem fazer pedidos</p>
                <p className="text-xs text-amber-600">Adicione pelo menos um produto ao menu para comecar a receber pedidos.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100 h-8 text-xs" onClick={() => navigate("/menu")} data-testid="go-to-menu-btn">
                Ir ao Menu
              </Button>
              <button onClick={() => { setDismissedEmptyMenu(true); sessionStorage.setItem("dismissed_empty_menu", "1"); }}
                className="text-amber-400 hover:text-amber-600" data-testid="dismiss-empty-menu">
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}
      {!dismissedEmailVerify && restaurant?.email_verified === false && (
        <EmailVerifyBanner
          email={restaurant.email}
          authAxios={authAxios}
          onDismiss={() => { setDismissedEmailVerify(true); sessionStorage.setItem("dismissed_email_verify", "1"); }}
        />
      )}
    </>
  );
}
